<template>
  <div class="result-view-wrapper bg-white h-[100dvh] flex flex-col justify-between p-8" v-if="!loading">
    <div class="logo-wrapper">
      <img src="@/assets/images/logo-primary.svg" alt="" width="91" />
    </div>
    <div v-if="paymentInfo['status'] === 'success'" class="flex flex-col w-2/3 mx-auto gap-4 items-center justify-center">
      <div class="flex bg-primary justify-center items-center w-20 h-20 rounded-full">
        <IconReceiptText />
      </div>
      <div class="text-2xl font-medium text-center">{{ t('success-payment-title') }}!</div>
      <div class="hint w-full text-center mt-5">{{ t('payment-details') }}</div>
      <div class="details-card flex flex-col gap-3 text-sm rounded-2xl bg-gray-50 border border-gray-200 w-[150%] p-4">
        <div class="flex justify-between">
          <div>{{ t('payment-id') }}:</div>
          <div>{{ paymentInfo['payment_id'] }}</div>
        </div>
        <div class="flex justify-between">
          <div>{{ t('payment-service') }}:</div>
          <div class="capitalize">{{ paymentInfo['payment_system'] }}</div>
        </div>
        <div class="flex justify-between" v-if="paymentInfo['amount']">
          <div>{{ t('payment-sum') }}:</div>
          <div>{{ formatBalance(paymentInfo['amount'], true) }} {{ t('sum') }}</div>
        </div>
        <div class="flex justify-between" v-if="paymentInfo['datetime']">
          <div>{{ t('payment-datetime') }}</div>
          <div>{{ paymentInfo['datetime'] }}</div>
        </div>
      </div>
    </div>
    <div v-else-if="(paymentInfo['status'] === 'new' || paymentInfo['status'] === 'draft')">
      <div v-if="paymentProcessing" class="payment-processing flex flex-col mx-auto w-2/3 gap-3 items-center justify-center">
        <div class="payment-processing-icon items-center justify-center flex bg-primary w-20 h-20 rounded-full">
          <IconLoading class="ms-3" color="#F05039" space="#FFFFFF" />
        </div>
        <div class="text-xl font-medium text-center">{{ t('draft-payment-title') }}</div>
        <div class="payment-processing-title text-base font-gray-600 text-center">{{ t('draft-payment-text') }}</div>
      </div>
      <div v-else class="payment-processing text-center flex flex-col gap-4">
        <div class="payment-processing-icon flex items-center justify-center gap-4">
          <div v-if="paymentInfo['status'] === 'success'">
            <div class="flex bg-primary justify-center items-center w-20 h-20 rounded-full">
              <IconReceiptText />
            </div>
          </div>
          <div v-else-if="['error', 'revert'].includes(paymentInfo['status'])">error icon</div>
          <div v-else class="payment-processing-icon items-center justify-center flex bg-primary w-20 h-20 rounded-full">
            <IconLoading v-if="statusLoading" class="ms-3" color="#F05039" space="#FFFFFF" />
            <div v-else class="flex bg-primary justify-center items-center w-20 h-20 rounded-full">
              <IconReceiptText />
            </div>
          </div>
        </div>
        <div class="text-2xl font-medium text-center">
          <span v-if="paymentInfo['status'] === 'success'">{{ t('success-payment-title') }}!</span>
          <span v-if="['error', 'revert'].includes(paymentInfo['status'])">{{ t('error-payment-title') }}</span>
          <span v-else-if="!statusLoading">{{ t('retry-payment-title') }}</span>
        </div>
      </div>
    </div>
    <div v-else class="flex flex-col justify-center items-center gap-4">
      <div class="payment-processing-icon items-center justify-center flex bg-primary w-20 h-20 rounded-full">
        <IconWarning class="w-8 h-8" color="#FFFFFF" />
      </div>
      <div class="text-lg font-medium text-center">{{ t('error-payment-text') }}</div>
    </div>
    <div class="flex flex-col gap-2 text-center">
      <div
        @click="openWindow(paymentInfo['receipt_url'])"
        v-if="paymentInfo['status'] === 'success' && paymentInfo['receipt_url']"
        class="bg-white gap-1 border border-primary flex items-center justify-center p-5 px-1 w-full rounded-2xl text-primary"
      >
        <IconReceipt color="#F05039" />
        <span class="self-center leading-6">{{ t("download-check") }}</span>
      </div>
      <div
        @click="openWindow(paymentInfo['tax_receipt_url'])"
        v-if="paymentInfo['status'] === 'success' && paymentInfo['tax_receipt_url']"
        class="bg-primary gap-1 flex border border-primary items-center justify-center p-5 px-1 w-full rounded-2xl text-white"
      >
        <IconReceipt />
        <span class="self-center leading-6">{{ t("download-fiscal") }}</span>
      </div>
      <div
        v-if="['rahmat', 'korzinkaGo'].includes(useragent)"
        @click="close"
        class="success-button flex gap-2 justify-center items-center border border-primary bg-white text-primary rounded-2xl text-center py-3"
      >
        <IconCloseCircle color="#F05039" />
        <span class="flex leading-10">{{ t('close') }}</span>
      </div>
      <div
        v-if="paymentInfo['table_id'] || paymentInfo['takeaway'] || paymentInfo['delivery']"
        @click="backToMenu(paymentInfo)"
        class="success-button flex gap-2 justify-center items-center border border-primary bg-white text-primary rounded-2xl text-center py-3"
      >
        <IconLineArrowLeft color="#F05039" />
        <span class="flex leading-10">{{ t('return') }}</span>
      </div>
      <div
        @click="reloadStatus"
        v-if="['new', 'draft'].includes(paymentInfo['status']) && !paymentProcessing && !statusLoading"
        class="bg-white gap-1 border border-primary flex items-center justify-center p-5 px-1 w-full rounded-2xl text-primary"
      >
        <span class="self-center leading-6">{{ t('reload-payment-status') }}</span>
      </div>
    </div>
  </div>
  <div v-else class="bg-white absolute w-full h-full" style="z-index: 10000">
    <LoaderWindow />
  </div>
</template>

<script setup>
import {useStore} from "vuex"
import {useI18n} from "vue-i18n"
import Service from "@/request/service.js"
import {useUseragent} from "@/helpers/useragent.js"
import LoaderWindow from "@/components/LoaderWindow.vue"
import {computed, onMounted, ref, watchEffect } from "vue"
import IconLoading from "@/components/icons/IconLoading.vue"
import IconReceipt from "@/components/icons/IconReceipt.vue"
import IconWarning from "@/components/icons/IconWarning.vue"
import { onBeforeRouteLeave, useRoute, useRouter } from "vue-router"
import IconReceiptText from "@/components/icons/IconReceiptText.vue"
import IconCloseCircle from '@/components/icons/IconCloseCircle.vue'
import IconLineArrowLeft from '@/components/icons/IconLineArrowLeft.vue'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const vuexStore = useStore()
const loading = ref(false)
const paymentInfo = ref({
  id: null,
  status: null,
  return_url: null,
  payment_system: null
})
const statusLoading = ref(false)
const paymentProcessing = ref(false)

onBeforeRouteLeave((_, from, next) => {
  vuexStore.dispatch('setHomeRoute', from.fullPath)
  next()
})
onMounted(() => {
  vuexStore.dispatch('setHomeRoute', route.fullPath)
  loading.value = true
  Service.paymentStatus(route.query.id, route.query.system).then(({ data: response }) => {
    if (response?.success) {
      checkAndRedirectToMenu(response.data['payment_info'])
      // employee.value = response.data['employee']
      // storeLogo.value = response.data['store_logo']
      if (response.data['payment_info']) {
        paymentInfo.value = response.data['payment_info']
      }
    }
  }).finally(() => {
    loading.value = false
    checkStatus()
  })
})

const backToMenu = payment => {
  document.body.style.overflow = null
  document.body.style.height = null
  if (payment.table_id) {
    router.push({
      name: 'order-table-view',
      params: { table: payment['table_id'] }
    })
    return true
  }
  const order = localStorage.getItem('order')
  let guid
  let type = null
  if (payment?.takeaway) {
    type = 'takeaway'
  } else if (payment?.delivery) {
    type = 'marketplace'
  }
  if (order) {
    const _order = JSON.parse(order)
    const _first = _order.find(() => true)
    guid = _first?.order_guid
  }
  router.push({
    name: 'order-view',
    params: {
      type,
      guid,
      store: payment['store_id'],
    }
  })
}

const checkAndRedirectToMenu = info => {
  if (info?.takeaway || info?.delivery || info?.table_id) {
    setTimeout(() => {
      backToMenu(info)
    }, 5000)
  }
}

const checkStatus = () => {
  if (paymentInfo.value['status'] !== 'success'
      || paymentInfo.value['status'] !== 'error'
      || paymentInfo.value['status'] !== 'revert') {
    paymentProcessing.value = true
    setTimeout(() => {
      Service.paymentStatus(paymentInfo.value['id'], paymentInfo.value['payment_system']).then((res) => {
        checkAndRedirectToMenu(res.data?.data?.['payment_info'])
        if (res.data?.data?.['payment_info']) {
          paymentInfo.value = res.data?.data?.['payment_info']
        }
      }).finally(() => {
        paymentProcessing.value = false
      })
    }, 5000)
  }
}

const openWindow = url => {
  if (useragent.value === 'rahmat') {
    window.flutterChannel.postMessage(
      JSON.stringify({
        action: 'browser',
        payload: { url }
      })
    )
  } else {
    window.open(url, '_blank')
  }
}

const close = () => {
  if (useragent.value === 'rahmat') {
    closeRahmatWindow()
  }
  if (useragent.value === 'korzinkaGo') {
    closeKorzinkaGoWindow()
  }
}

const closeRahmatWindow = () => {
  window.flutterChannel.postMessage(JSON.stringify({
    action: 'close'
  }))
}

const closeKorzinkaGoWindow = () => {
  window.parent.postMessage({ action: 'close' }, '*')
}

const reloadStatus = () => {
  statusLoading.value = true
  Service.paymentStatus(paymentInfo.value['id'], paymentInfo.value['payment_system']).then((res) => {
    checkAndRedirectToMenu(res.data?.data?.['payment_info'])
    if (res.data?.data?.['payment_info']) {
      paymentInfo.value = res.data?.data?.['payment_info']
    }
  }).finally(() => {
    statusLoading.value = false
  })
}

watchEffect(() => {
  if (paymentInfo.value['status'] === 'success' && paymentInfo.value['return_url']) {
    setTimeout(() => {
      window.location.href = paymentInfo.value['return_url']
    }, 5000)
  }
})

const useragent = computed(() => useUseragent().getUserAgent())
</script>

<style lang="scss">
body {
  background-color: #FFFFFF;
}
</style>
